import React, { useState } from "react";
import { supabase } from "../supabase";
import { MIN_PASSWORD_LENGTH } from "../helpers/authHelpers";
import { devLog } from "../helpers/devLog";
import "./CPInput.css";

/**
 * Password field with show/hide toggle.
 * Specialized composite that reuses canonical CP* chrome (.cp-input-group /
 * .cp-input-label / .cp-input) rather than inventing a parallel skin.
 */
function PasswordField({
  label,
  value,
  onChange,
  showPassword,
  onToggleShow,
  autoComplete,
  disabled,
}) {
  return (
    <div className="cp-input-group">
      <label className="cp-input-label">{label}</label>
      <div style={{ position: "relative" }}>
        <input
          className="cp-input"
          type={showPassword ? "text" : "password"}
          placeholder="Enter password"
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          disabled={disabled}
          style={{ paddingRight: "70px" }}
        />
        <button
          type="button"
          onClick={onToggleShow}
          style={{
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            border: "none",
            background: "transparent",
            color: "#d4af37",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          {showPassword ? "Hide" : "Show"}
        </button>
      </div>
    </div>
  );
}

export function UpdatePasswordScreen({ userEmail, onComplete, onBackToSignIn }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async () => {
    setFormError(null);

    if (!newPassword || !confirmPassword) {
      setFormError("Please enter and confirm your new password.");
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setFormError(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    devLog("[AUTH DEBUG] UpdatePasswordScreen: calling updateUser");

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error("[AUTH DEBUG] updateUser error:", error);
        setFormError(error.message || "Failed to update password.");
        return;
      }

      devLog("[AUTH DEBUG] updateUser success");
      setIsSuccess(true);
    } catch (err) {
      console.error("[AUTH DEBUG] UpdatePasswordScreen unexpected error:", err);
      setFormError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="screen nx-screen ci-success-screen">
        <div className="ci-success-content">
          <div className="ci-success-icon">
            <svg
              viewBox="0 0 52 52"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="checkmark-circle nx-checkmark-circle"
                cx="26"
                cy="26"
                r="24"
              />
              <path
                className="checkmark-check nx-checkmark-check"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
          </div>

          <h1 className="ci-success-title">Password Updated</h1>

          <p className="ci-success-sub">
            Your password has been changed. Please sign in with your new
            password.
          </p>

          <button
            type="button"
            className="nx-btn nx-btn-gold"
            style={{ marginTop: "12px", width: "100%", maxWidth: "320px" }}
            onClick={onComplete}
          >
            Continue to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen onboarding-screen">
      <div className="onboarding-content">
        <div className="onboarding-text">
          <h1 className="onboarding-title">Set New Password</h1>
          <p className="onboarding-subtitle">
            {userEmail
              ? `Create a new password for ${userEmail}`
              : "Create a new password for your account"}
          </p>
        </div>

        <div className="onboarding-actions">
          <PasswordField
            label="New Password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setFormError(null);
            }}
            showPassword={showNewPassword}
            onToggleShow={() => setShowNewPassword(!showNewPassword)}
            autoComplete="new-password"
            disabled={isSubmitting}
          />

          <PasswordField
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setFormError(null);
            }}
            showPassword={showConfirmPassword}
            onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
            autoComplete="new-password"
            disabled={isSubmitting}
          />

          {formError && <p className="nx-form-error">{formError}</p>}

          <button
            type="button"
            className="nx-btn nx-btn-gold"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating…" : "Update Password"}
          </button>

          <button
            type="button"
            className="nx-btn nx-btn-ghost"
            onClick={onBackToSignIn}
            disabled={isSubmitting}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
